/* eslint-disable */
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment'
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { Company } from './company.model';
import { SessionStorageService } from '../helpers/session-storage.service';
import { Customer } from '../customer/customer.model';
import { LatLng } from '../models/common/lat-lng.model';

export interface CompanyEmployeeInvitationRequest {
    name: string;
    emailAddress: string;
    phoneNumber: string;
    corporateRole: string;
    address?: string | null;
    countryId?: number | null;
    roleIds: number[];
}

export interface SelfRegistrationRequest {
    existingFirebaseId?: string | null;
    sendSetupEmail: boolean;
    customer: {
        name: string;
        emailAddress: string;
        phoneNumber: string;
        corporateRole: string;
        defaultLanguage: string;
        countryId?: number | null;
    };
    company: {
        name: string;
        crNumber: number;
        taxRegNumber: string;
        phoneNumber?: string | null;
        websiteURL?: string | null;
        emailAddress?: string | null;
        address?: string | null;
        placeId?: string | null;
        logo?: string | null;
        location?: LatLng | null;
        countryId?: number | null;
    };
    transportationAgency?: {
        enabled: boolean;
        motAgencyLicenseNumber?: string | null;
    };
    freightForwarder?: {
        enabled: boolean;
    };
}

export interface SelfRegistrationResponse {
    customer: Customer;
    company: Company;
    transportationAgencyId?: number | null;
    freightForwarderId?: number | null;
    setupEmailSent: boolean;
    requiresEmailSetup: boolean;
}

export type CompanyRegistrationAttachmentCategory =
    'VAT_CERTIFICATE' |
    'COMMERCIAL_REGISTRATION_CERTIFICATE' |
    'NATIONAL_ADDRESS';

export interface CompanyRegistrationAttachment {
    id?: number;
    ownerCompanyId?: number;
    companyId?: number;
    category: CompanyRegistrationAttachmentCategory;
    fileName: string;
    fileUrl: string;
    fileType?: string;
    fileSize?: number;
    uploadedAt?: Date;
    uploadedByCustomerId?: number;
    uploadedByCustomerName?: string;
}

export const COMPANY_REGISTRATION_ATTACHMENT_TYPES: { label: string; value: CompanyRegistrationAttachmentCategory }[] = [
    { label: 'VAT certificate', value: 'VAT_CERTIFICATE' },
    { label: 'Commercial Registration Certificate', value: 'COMMERCIAL_REGISTRATION_CERTIFICATE' },
    { label: 'National Address', value: 'NATIONAL_ADDRESS' }
];

@Injectable({ providedIn: 'root' })
export class CompanyService {

    private readonly http = inject(HttpClient);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly restApiUrl = `${environment.restApiUrl}`;

    getCompanyById(companyId: number) {
        return this.http.get<APIResponseWrapper<Company>>(`${this.restApiUrl}company-endpoint/companies?companyId=${companyId}`);
    }

    getCompaniesByCountry(countryId: number) {
        return this.http.get<Company[]>(`${this.restApiUrl}company-endpoint/companies-by-country?countryId=${countryId}`);
    }

    addCompany(company: Company) {
        const url = `${this.restApiUrl}company-endpoint/company`;
        return this.http.post<APIResponseWrapper<Company>>(url, company);
    }

    updateCompany(company: Company) {
        const url = `${this.restApiUrl}company-endpoint/company`;
        return this.http.put<APIResponseWrapper<Company>>(url, company);
    }

    getCompanyByCrNumber(crNumber: string) {
        return this.http.get<APIResponseWrapper<Company>>(`${this.restApiUrl}company-endpoint/companies?crNumber=${crNumber}`);
    }

    inviteEmployee(companyId: number, request: CompanyEmployeeInvitationRequest) {
        return this.http.post<APIResponseWrapper<Customer>>(
            `${this.restApiUrl}company-endpoint/${companyId}/employee-invitations`,
            request
        );
    }

    selfRegister(request: SelfRegistrationRequest) {
        return this.http.post<APIResponseWrapper<SelfRegistrationResponse>>(
            `${this.restApiUrl}company-endpoint/self-registration`,
            request
        );
    }

    getCompanyRegistrationAttachments(ownerCompanyId: number, companyId: number = this.sessionStorageService.getCompany()?.id || ownerCompanyId) {
        return this.http.get<CompanyRegistrationAttachment[]>(`${this.restApiUrl}crm/company/${ownerCompanyId}/registration-attachments?companyId=${companyId}`);
    }

    saveCompanyRegistrationAttachment(ownerCompanyId: number, attachment: CompanyRegistrationAttachment, companyId: number = this.sessionStorageService.getCompany()?.id || ownerCompanyId) {
        return this.http.post<APIResponseWrapper<CompanyRegistrationAttachment>>(`${this.restApiUrl}crm/company/${ownerCompanyId}/registration-attachments?companyId=${companyId}`, attachment);
    }

    deleteCompanyRegistrationAttachment(ownerCompanyId: number, attachmentId: number, companyId: number = this.sessionStorageService.getCompany()?.id || ownerCompanyId) {
        return this.http.delete<APIResponseWrapper<void>>(`${this.restApiUrl}crm/company/${ownerCompanyId}/registration-attachments/${attachmentId}?companyId=${companyId}`);
    }


}
